import {
  todoStepAncestorIds,
  type TodoStep,
} from "@lib/todoRail/todoRailModel";

/**
 * Which groups in the rail are open.
 *
 * Two sources, and the split is the point. Most expansion is DERIVED from where
 * the current step is, so the rail opens the group it is pointing into and lets
 * the others fall shut on their own — no trail of groups the funder never
 * opened. The explicit map is the funder's own say-so, and it always wins.
 */

/**
 * The groups that open by themselves: the ancestors of the current step, plus
 * that step's own group when it has one.
 */
export function autoExpandedGroupIds(
  steps: TodoStep[],
  targetStepId: string | null | undefined,
  targetHasChildren = false,
): Set<string> {
  const ids = new Set(todoStepAncestorIds(steps, targetStepId));
  // The target's OWN group opens too, not just the groups above it. A top-level
  // step has no ancestors, so ancestors-only left the current track sitting
  // collapsed over the very rows it was pointing at.
  if (targetStepId != null && targetHasChildren) ids.add(targetStepId);
  return ids;
}

/** An explicit toggle always wins; otherwise the derived answer stands. */
export function isGroupExpanded(
  explicit: Record<string, boolean>,
  auto: Set<string>,
  stepId: string,
): boolean {
  return explicit[stepId] ?? auto.has(stepId);
}

/**
 * Pins every group holding `stepId` open, because the funder just acted on that
 * row.
 *
 * Auto-expansion follows the CURRENT step, so completing the last unfinished
 * step in a group hands current to the next group and pulls this one shut —
 * taking the row that was just ticked off the screen with it. From the funder's
 * seat the menu item did nothing at all: no tick, no row, no answer. Acting on a
 * row must never move the row.
 */
export function pinGroupsOpen(
  explicit: Record<string, boolean>,
  steps: TodoStep[],
  stepId: string,
): Record<string, boolean> {
  const ancestors = todoStepAncestorIds(steps, stepId);
  if (ancestors.length === 0) return explicit;

  const next = { ...explicit };
  for (const id of ancestors) next[id] = true;
  return next;
}
